import React from "react";

import {DevicePhoneMobileIcon} from "@heroicons/react/24/outline";

import * as orm from "@client/features/orm";

import * as graphics from "@client/app/graphics";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";

import {SimpleIcon} from "@client/components/Icon";
import type {Props} from "@client/scenes/Journal";
import {useSiteContext} from "@client/site/context";
import {classNames} from "@reactivated";

export function Welcome(props: Props) {
    const context = useSiteContext();
    const alreadySetup = props.profile.assignments.length > 0;
    const getApp = () => {
        void analytics.track("CLICKED_DOWNLOAD_APP_FROM_WELCOME");

        if (context.mobile_os == "android") {
            window.location.href =
                "https://play.google.com/store/apps/details?id=com.joyapp.android&utm_content=thejosephabell-sprinkles";
        } else {
            window.location.href =
                "https://apps.apple.com/app/apple-store/id955172784?pt=120882043&ct=thejosephabell-sprinkles&mt=8";
        }
    };

    return (
        <div className="flex h-full flex-col justify-center py-8">
            {/* Header */}
            <div className="mb-4 text-center">
                <div className="flex flex-col items-center">
                    <graphics.DarkLogoWide className="size-72 h-auto" />
                    <p className="mt-1 text-sm text-gray-500">
                        Take control of your health
                    </p>
                </div>
            </div>

            {/* Recipe Saved Confirmation */}

            {/* Next Steps */}
            <div className="mb-6 space-y-4">
                <div
                    className="
                        rounded-2xl border border-orange-100 bg-orange-50 px-3 py-4
                    "
                >
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-orange-200
                            "
                        >
                            <SimpleIcon
                                icon="0532-chef"
                                className="text-lg text-orange-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                                Recipe saved
                            </h3>
                            <p className="text-xs text-gray-500">
                                {props.profile.user.registration_recipe_name}
                            </p>
                        </div>
                        <div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-orange-100
                            "
                        >
                            <SimpleIcon
                                icon="custom-bolder-check"
                                className="text-sm text-orange-600"
                            />
                        </div>
                    </div>
                </div>

                {/* Goal Setting */}
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-blue-200
                            "
                        >
                            <SimpleIcon
                                icon="0625-archery"
                                className="text-lg text-blue-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">
                                Set your goals
                            </h4>
                            <p className="text-xs text-gray-500">
                                Personalize your experience
                            </p>
                        </div>
                        {alreadySetup ? (
                            <div
                                className="
                                    flex size-8 items-center justify-center rounded-full
                                    bg-blue-100
                                "
                            >
                                <SimpleIcon
                                    icon="custom-bolder-check"
                                    className="text-sm text-blue-600"
                                />
                            </div>
                        ) : (
                            <div
                                className="
                                    w-14 rounded-full bg-blue-100 px-3 py-1.5
                                    text-center text-xs font-medium text-blue-600
                                "
                            >
                                Next
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile App */}
                <div
                    className="
                        rounded-2xl border border-purple-100 bg-purple-50 px-3 py-4
                    "
                >
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-purple-200
                            "
                        >
                            <DevicePhoneMobileIcon className="size-5 text-lg text-purple-600" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">
                                Get the mobile app
                            </h4>
                            <p className="text-xs text-gray-500">
                                Scan foods, log workouts, track steps
                            </p>
                        </div>
                        <div
                            className="
                                w-14 rounded-full bg-purple-100 px-3 py-1.5 text-center
                                text-xs font-medium text-purple-600
                            "
                        >
                            {alreadySetup ? "Next" : "Best"}
                        </div>
                    </div>
                </div>

                {/* Email Setup */}
                <div className="rounded-2xl bg-rose-50 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-rose-200
                            "
                        >
                            <SimpleIcon
                                icon="0105-shield-check"
                                className="text-lg text-rose-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">
                                Secure your account
                            </h4>
                            <p className="text-xs text-gray-500">
                                Check email to set password
                            </p>
                        </div>
                        {props.profile.user.must_verify_email == true ? (
                            <div
                                className="
                                    w-14 rounded-full bg-rose-100 px-3 py-1.5
                                    text-center text-xs font-medium text-rose-500
                                "
                            >
                                Later
                            </div>
                        ) : (
                            <div
                                className="
                                    flex size-8 items-center justify-center rounded-full
                                    bg-rose-100
                                "
                            >
                                <SimpleIcon
                                    icon="custom-bolder-check"
                                    className="text-sm text-rose-600"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
                {/* Primary Action */}
                {alreadySetup == false ? (
                    <button
                        onClick={() => {
                            void analytics.track("CLICKED_SET_GOALS_FROM_WELCOME");
                            router.push(
                                "goals.index",
                                {
                                    key: props.resolved.journal.day.key,
                                },
                                props.resolved,
                            );
                        }}
                        className="
                            w-full rounded-xl bg-indigo-500 py-4 font-medium text-white
                            transition-colors
                        "
                    >
                        Set up your goals
                    </button>
                ) : (
                    <button
                        onClick={getApp}
                        className="
                            flex w-full items-center justify-center rounded-xl
                            bg-indigo-600 py-4 font-medium text-white transition-colors
                        "
                    >
                        <SimpleIcon
                            icon={
                                context.mobile_os == "android"
                                    ? "custom-play-store"
                                    : "custom-app-store"
                            }
                            className="-mt-0.5 mr-1 -ml-3 size-6"
                        />
                        Get App
                    </button>
                )}

                {/* Secondary Actions */}
                <div className="grid grid-cols-2 gap-3">
                    {alreadySetup == false && (
                        <button
                            onClick={getApp}
                            className="
                                flex items-center justify-center rounded-xl bg-gray-900
                                px-4 py-3 text-sm font-medium text-white
                                transition-colors
                                hover:bg-gray-800
                            "
                        >
                            <SimpleIcon
                                icon={
                                    context.mobile_os == "android"
                                        ? "custom-play-store"
                                        : "custom-app-store"
                                }
                                className="mr-2 -ml-2 size-5"
                            />
                            Get App
                        </button>
                    )}

                    <button
                        onClick={() => {
                            void analytics.track("SKIPPED_SETUP_FROM_WELCOME");
                            props.dispatch({type: "HIDE_SETUP"});

                            if (alreadySetup == true) {
                                void analytics.track("SKIPPED_SETUP_TWICE");
                                void props.dispatch(
                                    orm.dismissAnnouncement("HIDE_RECIPE_REGISTRATION"),
                                );
                            }
                        }}
                        className={classNames(
                            "bg-stone-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-stone-300 transition-colors text-sm font-medium",
                            alreadySetup == true && "col-span-2",
                        )}
                    >
                        Skip & explore
                    </button>
                </div>
            </div>

            {/* Footer */}
            <p className="mt-2 text-center text-xs/relaxed text-gray-600">
                Nutrition <span className="text-indigo-700">•</span> Workouts{" "}
                <span className="text-indigo-700">•</span> Habits
            </p>
        </div>
    );
}
